import React, { useState } from "react";
import { FormControl, FormHelperText, MenuItem, Select } from "@material-ui/core";
import { FormDateField } from "../FormComponents/FormDateField";
import {
    consolidateDateToNumber,
    convertDateStringToNumber,
    mapNumberToDate,
    today
} from "../../../utilities/dateTimeUtils";
import { DATE_FORMAT } from "../../../constants/TIME";
import moment from "moment";

export type IVisibilityType = "start_date" | "plus_2_years" | "plus_7_days" | "unrestricted" | "custom";

export interface IVisibilityCategory {
    type: IVisibilityType;
    title: string;
    condition: (date: number, visibilityDate: number) => boolean;
    calculateVisibility: (date: number) => number;
}

export const allVisibilityCategories: IVisibilityCategory[] = [
    {
        type: "start_date",
        title: "Start date",
        condition: (date, visibilityDate) => {
            if (date === 0) return false;
            return date === visibilityDate;
        },
        calculateVisibility: (date: number): number => date
    },
    {
        type: "plus_2_years",
        title: "+ 2 years",
        condition: (date, visibilityDate) => {
            const momentDate = moment(date, DATE_FORMAT);
            const momentVisibilityDate = moment(visibilityDate, DATE_FORMAT);
            return momentDate.isSame(momentVisibilityDate.subtract(2, "years"));
        },
        calculateVisibility: (date: number): number => {
            const momentDate = moment(date, DATE_FORMAT);
            return convertDateStringToNumber(momentDate.add(2, "years").format(DATE_FORMAT));
        }
    },
    {
        type: "plus_7_days",
        title: "+ 7 days",
        condition: (date, visibilityDate) => {
            const momentDate = moment(date, DATE_FORMAT);
            const momentVisibilityDate = moment(visibilityDate, DATE_FORMAT);
            return momentDate.isSame(momentVisibilityDate.subtract(7, "days"));
        },
        calculateVisibility: date => {
            const momentDate = moment(date, DATE_FORMAT);
            return convertDateStringToNumber(momentDate.add(7, "days").format(DATE_FORMAT));
        }
    },
    {
        type: "unrestricted",
        title: "Unrestricted",
        condition: (date, visibilityDate) => visibilityDate === 0,
        calculateVisibility: (date: number): number => 0
    },
    {
        type: "custom",
        title: "Custom",
        condition: () => true,
        calculateVisibility: (date: number): number => date
    }
];

export interface IVisibilitySelectProps {
    error?: boolean;
    helperText?: string;
    categories: IVisibilityType[];
    value: IVisibilityType;
    onChange: (value, newDate?) => void;
    date?: number;
    onDateChange?: (newDate) => void;
};

export const VisibilitySelect: React.FunctionComponent<IVisibilitySelectProps> = ({
    error,
    helperText,
    categories,
    value,
    onChange,
    date,
    onDateChange
}: IVisibilitySelectProps) => {
    const [isCustom, setIsCustom] = useState<boolean>(false);

    return (
        <FormControl error={error}>
            <Select
                labelId="visibility_select"
                id="visibility_select"
                value={isCustom ? "custom" : value}
                onChange={(event: any) => {
                    const actualDate = event.target.value === "custom" ? (date || convertDateStringToNumber(today().format(DATE_FORMAT))) : null;
                    onChange(event.target.value, actualDate);
                    setIsCustom(event.target.value === "custom");
                }}
            >
                {categories?.map(o => {
                    const category = allVisibilityCategories.find(c => c.type === o);
                    return (
                        <MenuItem value={category.type} key={category.type}>
                            {category.title}
                        </MenuItem>
                    );
                })}
            </Select>
            {(isCustom || value === "custom") && (
                <FormDateField
                    disabled={false}
                    onChange={newDate => onDateChange(consolidateDateToNumber(newDate))}
                    placeholder=""
                    value={date !== 0 ? mapNumberToDate(date).format(DATE_FORMAT) : today().format(DATE_FORMAT)}
                    allowFuture
                />
            )}
            <FormHelperText>{helperText}</FormHelperText>
        </FormControl>
    );
};
